import { useEffect, useState } from "react"
import { Shell } from "@/components/Shell"
import {
  createIngredient,
  deleteIngredient,
  searchIngredients,
  type Ingredient,
} from "@/lib/api"

export function IngredientsPage() {
  const [q, setQ] = useState("")
  const [rows, setRows] = useState<Ingredient[]>([])
  const [adding, setAdding] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = async (query = q) => {
    try {
      setLoading(true)
      setRows(await searchIngredients(query))
      setError(null)
    } catch (e: any) {
      setError(e?.message ?? "Load failed")
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    void load("")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // debounced reload on q change
  useEffect(() => {
    const t = setTimeout(() => void load(q), 200)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q])

  return (
    <Shell
      currentPath="/ingredients"
      navItems={[
        { href: "/", label: "Dashboard" },
        { href: "/today", label: "Today" },
        { href: "/performance", label: "Performance" },
        { href: "/ingredients", label: "Ingredients" },
        { href: "/recipes", label: "Recipes" },
        { href: "/weight", label: "Weight" },
        { href: "/profile", label: "Profile" },
      ]}
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        <p className="eyebrow mb-3">ingredients · per-100g library</p>
        <h1 className="font-display text-display tracking-tightest mb-10">
          Your <span className="italic text-moss">pantry</span>, indexed.
        </h1>

        <div className="flex items-center gap-4 mb-8">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search (chickpeas, salmon, oats…)"
            className="flex-1 bg-transparent border-0 border-b border-border focus:border-moss focus:outline-none px-0 py-3 font-sans text-base"
          />
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="bg-foreground text-background px-5 py-2.5 rounded font-sans text-sm hover:bg-moss transition-colors whitespace-nowrap"
          >
            + Add
          </button>
        </div>

        {error && <p className="font-sans text-sm text-persimmon mb-4">{error}</p>}

        {loading ? (
          <p className="font-mono text-xs text-muted-foreground">loading…</p>
        ) : rows.length === 0 ? (
          <p className="font-sans text-sm text-muted-foreground">No matches. Try a different search, or add a new ingredient.</p>
        ) : (
          <IngredientTable rows={rows} onDelete={async (id) => { await deleteIngredient(id); load() }} />
        )}

        {adding && (
          <AddIngredientModal
            onClose={() => setAdding(false)}
            onSaved={(ing) => {
              setAdding(false)
              setRows([ing, ...rows.filter((r) => r.id !== ing.id)])
            }}
          />
        )}
      </div>
    </Shell>
  )
}

function IngredientTable({
  rows, onDelete,
}: {
  rows: Ingredient[]
  onDelete: (id: string) => Promise<void>
}) {
  return (
    <div className="border-t border-b border-border">
      <div className="grid grid-cols-[1fr_auto_auto_auto_auto_auto_auto] gap-4 py-3 px-1 eyebrow border-b border-border">
        <span>name</span>
        <span className="text-right">kcal</span>
        <span className="text-right">protein</span>
        <span className="text-right">fiber</span>
        <span className="text-right">carbs</span>
        <span className="text-right">sugar</span>
        <span className="text-right">source</span>
      </div>
      <ul className="divide-y divide-border">
        {rows.map((r) => (
          <li key={r.id} className="grid grid-cols-[1fr_auto_auto_auto_auto_auto_auto] gap-4 py-3 px-1 items-center font-mono text-xs tabular-nums text-muted-foreground">
            <div className="min-w-0">
              <p className="font-sans text-sm text-foreground truncate">{r.name}</p>
              <p className="text-xs">{[r.state, r.category].filter(Boolean).join(" · ")}</p>
            </div>
            <span className="text-right">{r.calories_per_100g.toFixed(0)}</span>
            <span className="text-right">{r.protein_per_100g.toFixed(1)}</span>
            <span className="text-right">{r.fiber_per_100g.toFixed(1)}</span>
            <span className="text-right">{r.carbs_per_100g.toFixed(1)}</span>
            <span className="text-right">{r.sugar_per_100g.toFixed(1)}</span>
            <span className="text-right flex items-center gap-2 justify-end">
              <SourceBadge source={r.source} shared={r.shared} />
              {!r.shared && (
                <button onClick={() => onDelete(r.id)} className="hover:text-persimmon" title="Delete">×</button>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function SourceBadge({ source, shared }: { source: string; shared: boolean }) {
  const color = shared
    ? "border-border text-muted-foreground"
    : source === "usda"
      ? "border-moss/40 text-moss"
      : source === "claude"
        ? "border-clay/40 text-clay"
        : "border-border text-muted-foreground"
  return (
    <span className={`px-2 py-[2px] border ${color} rounded text-[10px] uppercase tracking-smallcaps`}>
      {shared ? "shared" : source}
    </span>
  )
}

function AddIngredientModal({
  onClose, onSaved,
}: {
  onClose: () => void
  onSaved: (i: Ingredient) => void
}) {
  const [name, setName] = useState("")
  const [useUSDA, setUseUSDA] = useState(true)
  const [state, setState] = useState("cooked")
  const [category, setCategory] = useState("")
  const [manual, setManual] = useState({ fiber: 0, sugar: 0, protein: 0, carbs: 0, calories: 0 })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      const out = await createIngredient({
        name,
        state,
        category,
        lookup_via_usda: useUSDA,
        fiber_per_100g: manual.fiber,
        sugar_per_100g: manual.sugar,
        protein_per_100g: manual.protein,
        carbs_per_100g: manual.carbs,
        calories_per_100g: manual.calories,
      })
      onSaved(out)
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || "Save failed")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-foreground/40 grid place-items-center p-6 z-20">
      <form
        onSubmit={onSubmit}
        className="bg-background border border-border rounded max-w-lg w-full p-8 space-y-5"
      >
        <div className="flex items-baseline justify-between">
          <h2 className="font-display text-2xl tracking-tight">Add an ingredient</h2>
          <button type="button" onClick={onClose} className="eyebrow">close</button>
        </div>

        <label className="block">
          <span className="eyebrow mb-2 block">name</span>
          <input
            type="text"
            required
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. peanut butter"
            className="w-full bg-transparent border-0 border-b border-border focus:border-moss focus:outline-none py-2 font-sans"
          />
        </label>

        <div className="flex gap-4">
          <label className="flex-1">
            <span className="eyebrow mb-2 block">state</span>
            <select value={state} onChange={(e) => setState(e.target.value)}
              className="w-full bg-transparent border-b border-border py-2 font-sans">
              {["raw","cooked","baked","dry","roasted","as-is"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="flex-1">
            <span className="eyebrow mb-2 block">category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)}
              className="w-full bg-transparent border-b border-border py-2 font-sans">
              <option value="">—</option>
              {["high-fiber","medium-fiber","low-fiber","protein","dairy","pantry","snacks","beverages","other"].map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={useUSDA} onChange={(e) => setUseUSDA(e.target.checked)} />
          <span className="font-sans text-sm">
            Look up macros via USDA <span className="text-muted-foreground">(recommended)</span>
          </span>
        </label>

        {!useUSDA && (
          <div className="grid grid-cols-5 gap-3 pt-2 border-t border-border">
            {(["calories","protein","fiber","carbs","sugar"] as const).map((k) => (
              <label key={k} className="block">
                <span className="eyebrow mb-1 block">{k}</span>
                <input
                  type="number"
                  value={manual[k as keyof typeof manual]}
                  onChange={(e) => setManual({ ...manual, [k]: parseFloat(e.target.value) || 0 })}
                  className="w-full bg-transparent border-b border-border py-1.5 font-mono text-sm"
                />
              </label>
            ))}
            <p className="col-span-5 font-sans text-xs text-muted-foreground">All per 100g.</p>
          </div>
        )}

        {err && <p className="font-sans text-sm text-persimmon">{err}</p>}

        <div className="flex items-center gap-3 pt-2">
          <button type="submit" disabled={busy || !name}
            className="bg-foreground text-background px-5 py-2.5 rounded font-sans text-sm hover:bg-moss transition-colors disabled:opacity-50">
            {busy ? "Saving…" : "Add ingredient"}
          </button>
          <button type="button" onClick={onClose} className="eyebrow">cancel</button>
        </div>
      </form>
    </div>
  )
}
